<script setup>
import { onMounted, ref } from "vue";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SecuritySectionTitle from "@/components/security/SecuritySectionTitle.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import Notification from "@/utils/plugins/NotificationService";
import AuditLogService from "@/api/services/auditLog.service";
import { buildAuditLogParams } from "@/utils/auditLogParams";

// Catálogos fijos, reflejo de App\Enum\AuditLogCategory / App\Enum\AuditActorType
// del backend (no hay endpoint de catálogo para auditoría, a diferencia de los
// permisos módulo:acción — son enums pequeños y estables).
const categoryOptions = [
  { value: "auth", label: "Autenticación" },
  { value: "data_change", label: "Cambio de datos" },
  { value: "permission_change", label: "Cambio de permisos" },
  { value: "impersonation", label: "Impersonación" },
  { value: "api_key", label: "Aplicación externa (API key)" },
];
const actorTypeOptions = [
  { value: "user", label: "Empleado" },
  { value: "extranet_user", label: "Usuario externo" },
  { value: "api_application", label: "Aplicación externa" },
];
const categoryLabel = (value) => categoryOptions.find((c) => c.value === value)?.label ?? value ?? "—";
const actorTypeLabel = (value) => actorTypeOptions.find((a) => a.value === value)?.label ?? value ?? "—";

const page = ref(1);
const itemsPerPage = ref(10);
const totalResults = ref(0);
const auditEvents = ref([]);
const showEventDialog = ref(false);
const selectedEvent = ref(null);
const filters = ref({
  dateRange: [],
  category: "",
  action: "",
  actorType: "",
  actorId: "",
});

const disableFutureDates = (date) => date.getTime() > new Date().setHours(23, 59, 59, 999);

const formatDateTime = (iso) => {
  const date = new Date(iso);
  return date.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
};

const actorLabel = (event) => {
  if (!event.actorType && !event.actorId) return "Sistema";
  const ref = `${actorTypeLabel(event.actorType)} #${event.actorId ?? "—"}`;
  return event.actorName ? `${event.actorName} (${ref})` : ref;
};

const actingAsLabel = (event) => {
  if (!event.actingAsType && !event.actingAsId) return null;
  const ref = `${actorTypeLabel(event.actingAsType)} #${event.actingAsId ?? "—"}`;
  return event.actingAsName ? `${event.actingAsName} (${ref})` : ref;
};

const load = () => {
  AuditLogService.getAuditLogs(buildAuditLogParams(filters.value, page.value, itemsPerPage.value)).then((res) => {
    if (!res) return;
    auditEvents.value = res.data["hydra:member"] ?? [];
    totalResults.value = res.data["hydra:totalItems"] ?? auditEvents.value.length;
  });
};

const search = () => {
  page.value = 1;
  load();
};

const openEvent = (event) => {
  selectedEvent.value = event;
  showEventDialog.value = true;
};

const closeEventDialog = () => {
  showEventDialog.value = false;
  selectedEvent.value = null;
};

const exportAudit = () =>
  Notification.info("La exportación de la auditoría estará disponible próximamente.", 3500);

onMounted(load);
</script>

<template>
  <section class="security-audit">
    <HeaderTitle title="Seguridad" />

    <SecuritySectionTitle title="Auditoría" />

    <el-row class="mt-2">
      <el-card class="card-search audit-filters w-100 wback" body-style="padding:0">
        <div class="search-form">
          <div class="row">
            <div class="col-4 el-col-xs-24">
              <div class="form-item date-picker">
                <label class="w-100" for="audit-date-range">Rango de fechas</label>
                <el-date-picker id="audit-date-range" v-model="filters.dateRange" name="audit-date-range"
                  class="w-100 audit-date-range" type="daterange" start-placeholder="Desde" end-placeholder="Hasta"
                  format="DD/MM/YYYY" value-format="YYYY-MM-DD" :picker-options="{ firstDayOfWeek: 1 }"
                  :disabled-date="disableFutureDates" clearable @change="search" />
              </div>
            </div>
            <div class="col-4 el-col-xs-24">
              <div class="form-item">
                <label class="w-100">Categoría</label>
                <el-select v-model="filters.category" class="w-100" placeholder="Todas" clearable filterable
                  @change="search">
                  <el-option v-for="c in categoryOptions" :key="c.value" :label="c.label" :value="c.value" />
                </el-select>
              </div>
            </div>
            <div class="col-4 el-col-xs-24">
              <div class="form-item">
                <label class="w-100">Acción (texto libre)</label>
                <el-input v-model="filters.action" class="w-100" placeholder="ej. login_failed" clearable
                  @change="search" @keyup.enter="search" />
              </div>
            </div>
          </div>
          <div class="row second-row">
            <div class="col-4 el-col-xs-24">
              <div class="form-item">
                <label class="w-100">Tipo de actor</label>
                <el-select v-model="filters.actorType" class="w-100" placeholder="Todos" clearable @change="search">
                  <el-option v-for="a in actorTypeOptions" :key="a.value" :label="a.label" :value="a.value" />
                </el-select>
              </div>
            </div>
            <div class="col-4 el-col-xs-24">
              <div class="form-item">
                <label class="w-100">ID del actor</label>
                <el-input v-model="filters.actorId" class="w-100" placeholder="ej. 17" clearable @change="search"
                  @keyup.enter="search" />
              </div>
            </div>
            <div class="col-4 el-col-xs-24 audit-filters__actions">
              <button type="button" class="btn btn_red" @click="search">BUSCAR</button>
              <button type="button" class="btn btn_yellow btn-search audit-filters__export" @click="exportAudit">
                EXPORTAR
              </button>
            </div>
          </div>
        </div>
      </el-card>
    </el-row>

    <div class="table table-not-mobile audit-table">
      <table>
        <thead>
          <tr>
            <th>Fecha y hora</th>
            <th>Actor</th>
            <th>Categoría</th>
            <th>Acción</th>
            <th>Entidad afectada</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="event in auditEvents" :key="event.id" class="pointer" role="button" tabindex="0"
            @click="openEvent(event)" @keydown.enter="openEvent(event)">
            <td>{{ formatDateTime(event.createdAt) }}</td>
            <td>
              <div class="audit-user">
                <span>{{ actorLabel(event) }}</span>
                <small v-if="actingAsLabel(event)">actuando como {{ actingAsLabel(event) }}</small>
              </div>
            </td>
            <td><span class="audit-status">{{ categoryLabel(event.category) }}</span></td>
            <td>{{ event.action }}</td>
            <td>{{ event.entityType ? `${event.entityType} #${event.entityId}` : "—" }}</td>
            <td>{{ event.ip ?? "—" }}</td>
          </tr>
        </tbody>
      </table>
      <p v-if="!auditEvents.length" class="empty">No se han encontrado eventos con los filtros seleccionados.</p>
    </div>

    <div class="table-mobile audit-cards">
      <el-card v-for="event in auditEvents" :key="event.id" class="audit-card" role="button" tabindex="0"
        @click="openEvent(event)" @keydown.enter="openEvent(event)">
        <div class="audit-card__header">
          <div class="audit-user">
            <span>{{ actorLabel(event) }}</span>
          </div>
          <span class="audit-status">{{ categoryLabel(event.category) }}</span>
        </div>
        <p><b>Fecha y hora:</b>{{ formatDateTime(event.createdAt) }}</p>
        <p><b>Acción:</b>{{ event.action }}</p>
        <p v-if="actingAsLabel(event)"><b>Actuando como:</b>{{ actingAsLabel(event) }}</p>
        <p><b>Entidad afectada:</b>{{ event.entityType ? `${event.entityType} #${event.entityId}` : "—" }}</p>
      </el-card>
      <p v-if="!auditEvents.length" class="empty">No se han encontrado eventos con los filtros seleccionados.</p>
    </div>

    <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" :total="totalResults"
      :page-size-options="[10, 20, 50]" class="pagination" @current-change="load" @size-change="page = 1; load()" />

    <el-dialog v-model="showEventDialog" :center="true" width="750px" :show-close="false" :lock-scroll="true"
      class="audit-dialog" style="border-radius:20px" @close="closeEventDialog">
      <section v-if="selectedEvent" class="audit-dialog__content">
        <div class="audit-dialog__header">
          <div class="title-text ml-4">
            <svg class="audit-dialog__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
              <path d="M8.8 11.7 11 13.9l4.5-4.5" />
            </svg> Detalle del evento
          </div>
          <img class="audit-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt="Cerrar"
            @click="closeEventDialog" />
        </div>

        <div class="audit-dialog__summary">
          <div>
            <b>{{ selectedEvent.action }}</b>
            <small>{{ categoryLabel(selectedEvent.category) }}</small>
          </div>
        </div>

        <div class="row audit-dialog__details">
          <div class="col-md-6 el-col-xs-24"><b>Fecha y hora</b><span>{{ formatDateTime(selectedEvent.createdAt) }}</span></div>
          <div class="col-md-6 el-col-xs-24"><b>Actor</b><span>{{ actorLabel(selectedEvent) }}</span></div>
          <div v-if="actingAsLabel(selectedEvent)" class="col-md-6 el-col-xs-24"><b>Actuando como</b><span>{{ actingAsLabel(selectedEvent) }}</span></div>
          <div class="col-md-6 el-col-xs-24"><b>Categoría</b><span>{{ categoryLabel(selectedEvent.category) }}</span></div>
          <div class="col-md-6 el-col-xs-24"><b>Entidad afectada</b><span>{{ selectedEvent.entityType ? `${selectedEvent.entityType} #${selectedEvent.entityId}` : "—" }}</span></div>
          <div class="col-md-6 el-col-xs-24"><b>Dirección IP</b><span>{{ selectedEvent.ip ?? "—" }}</span></div>
          <div class="col-md-6 el-col-xs-24"><b>Dispositivo</b><span>{{ selectedEvent.userAgent ?? "—" }}</span></div>
          <div class="col-md-6 el-col-xs-24"><b>Request ID</b><span>{{ selectedEvent.requestId ?? "—" }}</span></div>
        </div>

        <div v-if="selectedEvent.changes && Object.keys(selectedEvent.changes).length" class="audit-dialog__changes">
          <h3>Cambios realizados</h3>
          <div v-for="(change, field) in selectedEvent.changes" :key="field" class="audit-change">
            <b>{{ field }}</b><span>{{ change.before ?? "—" }}</span><font-awesome-icon icon="arrows-rotate" />
            <span>{{ change.after ?? "—" }}</span>
          </div>
        </div>
        <p v-else class="audit-dialog__empty">Este evento no contiene cambios adicionales.</p>

        <div class="audit-dialog__actions">
          <button type="button" class="btn btn_yellow" @click="closeEventDialog">CERRAR</button>
        </div>
      </section>
    </el-dialog>
  </section>
</template>

<style lang="scss" scoped>
.security-audit {
  color: #30385d;
}

.audit-filters {
  background: #fff !important;
  background-image: none !important;
  border-radius: 15px;
}

.audit-filters__actions {
  align-items: center;
  align-self: start;
  display: flex;
  gap: 14px;
  padding-top: 20px;
}

.audit-filters__actions .btn {
  align-items: center;
  color: #fff !important;
  display: flex;
  flex: 1;
  font-weight: 800;
  height: 44px;
  justify-content: center;
  margin: 0 !important;
  min-width: 0;
  width: auto;
}

.audit-filters__actions .audit-filters__export {
  color: #474ce6 !important;
}

.audit-table table {
  min-width: 980px;
}

.audit-table th {
  text-transform: uppercase;
}

.audit-table td {
  vertical-align: middle;
}

.audit-date-range {
  width: 100%;
}

:deep(.audit-date-range.el-range-editor.el-input__wrapper) {
  border-radius: 20px !important;
  min-height: 42px;
}


.audit-user {
  display: grid;
  gap: 2px;
}

.audit-user small {
  color: #7c83a2;
}

.audit-status {
  background: #eef0ff;
  border: 1px solid #d8dcff;
  border-radius: 15px;
  color: #474ce6;
  font-size: .75rem;
  padding: 3px 10px;
  white-space: nowrap;
}

.table-mobile {
  display: none;
}

.pagination {
  display: flex;
  justify-content: center;
  margin: 16px 0 22px;
}

.empty {
  color: #7c83a2;
  text-align: center;
}

.audit-dialog :deep(.el-dialog) {
  border-radius: 20px;
  max-width: calc(100vw - 24px);
}

.audit-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.audit-dialog__header {
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
}

.audit-dialog__header .title-text {
  align-items: center;
  color: #474ce6;
  display: flex;
  font-size: 2.5em;
  font-weight: 900;
  letter-spacing: -.05em;
}

.audit-dialog__icon {
  fill: none;
  flex: 0 0 40px;
  height: 40px;
  margin-right: 12px;
  stroke: #474ce6;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  width: 40px;
}

.audit-dialog__close {
  height: 24px;
  object-fit: contain;
  width: 24px;
}

.audit-dialog__summary {
  align-items: center;
  border-bottom: 1px solid #e3e5f0;
  display: flex;
  gap: 12px;
  padding: 16px 0;
}

.audit-dialog__summary>div {
  display: grid;
  flex: 1;
  gap: 4px;
}

.audit-dialog__summary small {
  color: #7c83a2;
}

.audit-dialog__details {
  margin: 0;
  padding: 14px 0 4px;
}

.audit-dialog__details>div {
  display: grid;
  gap: 4px;
  margin-bottom: 14px;
}

.audit-dialog__details b,
.audit-dialog__changes h3 {
  color: #474ce6;
  font-size: .78rem;
  text-transform: uppercase;
}

.audit-dialog__details span {
  word-break: break-word;
}

.audit-dialog__changes {
  border-top: 1px solid #e3e5f0;
  padding-top: 12px;
}

.audit-dialog__changes h3 {
  margin: 0 0 10px;
}

.audit-change {
  align-items: center;
  background: #f7f8ff;
  border-radius: 10px;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(110px, .8fr) minmax(0, 1fr) 18px minmax(0, 1fr);
  padding: 10px;
}

.audit-change b {
  color: #474ce6;
}

.audit-change span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audit-change .svg-inline--fa {
  color: #7c83a2;
  font-size: .75rem;
}

.audit-dialog__empty {
  color: #7c83a2;
  margin: 18px 0;
}

.audit-dialog__actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 18px;
}

.audit-dialog__actions .btn {
  min-width: 160px;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }

  .second-row {
    margin-top: 20px;
  }
}

@media (max-width: 767px) {
  .table-not-mobile {
    display: none;
  }

  .audit-filters {
    background: #fff !important;
  }

  .audit-filters__actions {
    padding-top: 4px;
  }

  .audit-filters__actions .btn {
    width: auto;
  }

  .audit-cards {
    display: grid;
    gap: 12px;
  }

  .audit-cards :deep(.el-card__body) {
    padding: 14px;
  }

  .audit-card {
    cursor: pointer;
  }

  .audit-card__header {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: space-between;
  }

  .audit-card p {
    display: grid;
    gap: 7px;
    grid-template-columns: 130px minmax(0, 1fr);
    margin: 10px 0 0;
    word-break: break-word;
  }

  .audit-card p b {
    color: #474ce6;
  }

  .pagination {
    max-width: 100%;
    overflow: hidden;
  }

  .audit-dialog__header {
    align-items: flex-start;
    gap: 12px;
  }

  .audit-dialog__header .title-text {
    font-size: 26px;
    line-height: 1.15;
    margin-left: 0 !important;
  }

  .audit-change {
    grid-template-columns: minmax(0, 1fr) 18px minmax(0, 1fr);
  }

  .audit-change b {
    grid-column: 1 / -1;
  }

  .audit-dialog__actions {
    flex-direction: column;
  }

  .audit-dialog__actions .btn {
    width: 100%;
  }
}
</style>
